/**
 * useCurrencyFormat hook
 * Wraps currencyUtils with the current currency from CurrencyContext.
 * Use this in React components instead of calling currencyUtils directly.
 */
import { useCallback } from 'react';
import { useCurrency, CurrencyCode } from '../context/CurrencyContext';
import {
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyPrecise,
  currencyLabel,
  convertFromMAD,
} from '../utils/currencyUtils';

interface UseCurrencyFormatReturn {
  /** Format full number: "MAD 1,234,567" or "1,234,567 €" */
  fmtCurrency: (value: number | null | undefined) => string;
  /** Format compact: "MAD 1.2M", "$450K" */
  fmtCurrencyCompact: (value: number | null | undefined) => string;
  /** Format with 2 decimals: "MAD 1,234.56" */
  fmtCurrencyPrecise: (value: number | null | undefined) => string;
  /** Label for headers: "(MAD)", "(EUR)", "(USD)" */
  fmtCurrencyLabel: () => string;
  /** Convert a MAD value to the selected currency (raw number) */
  convertAmount: (value: number) => number;
  /** Current currency code */
  currency: CurrencyCode;
}

export const useCurrencyFormat = (): UseCurrencyFormatReturn => {
  const { currency } = useCurrency();

  const fmtCurrency = useCallback(
    (value: number | null | undefined) => formatCurrency(value, currency),
    [currency],
  );

  const fmtCurrencyCompact = useCallback(
    (value: number | null | undefined) => formatCurrencyCompact(value, currency),
    [currency],
  );

  const fmtCurrencyPrecise = useCallback(
    (value: number | null | undefined) => formatCurrencyPrecise(value, currency),
    [currency],
  );

  const fmtCurrencyLabel = useCallback(
    () => currencyLabel(currency),
    [currency],
  );

  const convertAmount = useCallback(
    (value: number) => convertFromMAD(value, currency),
    [currency],
  );

  return { fmtCurrency, fmtCurrencyCompact, fmtCurrencyPrecise, fmtCurrencyLabel, convertAmount, currency };
};
